import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators";
import { classMap } from "lit/directives/class-map";

/**
 *  @customElement 'k-tree'
 */
@customElement('k-tree')
export class KelydraTree extends LitElement  {


  static styles = [
    css`


    .tree-item {
    }
    
    .tree-item-header {
      display:flex;
      align-items:center;
    }


    .tree-item-header k-icon {
      cursor:pointer;
    }

    .tree-item-header k-icon.hidden {
      visibility:hidden;  
    }


    .tree-item-header .tree-item-caption {
      flex:1;
      overflow: hidden;
      white-space: nowrap;
      cursor: pointer;
    }

    .tree-item-header.selected .tree-item-caption {
      background:var(--tree-select-background, blue);
      color:var(--tree-select-color, white);
    }


    .tree-item-children {
      margin-left:var(--tree-ident, 16px);      
    }

    .tree-item.add {
      color:gray;
      font-size:smaller;
      --icon-size: 20px;
      --icon-color:gray;
      margin-left: 4px;
    }

    .tree-item.add k-icon {
      cursor:pointer;
    }


    `
  ]


  @property()
  public rootItem:ITreeItem;

  @property()
  public selectedItem:ITreeItem;

  @property({type:Boolean})
  public readonly:boolean = false;


  @property({type:Boolean})
  public dragAndDrop:boolean = false;

  public iconOpenName = "square-plus";
  public iconCloseName = "square-minus";
  public iconAddName = "document-add";

  protected dragItem:ITreeItem;


  public select(selected:ITreeItem, fireEvent:boolean = true) {
    if (this.selectedItem == selected) {
      return;
    }

    if (fireEvent) {
      let detail:TreeSelectElementEvent = {
        item:selected,
        veto:false
      }
      let treeSelectElementEvent = new CustomEvent<TreeSelectElementEvent>('tree-select-item',{detail:detail});
      this.dispatchEvent(treeSelectElementEvent);
  
      if (detail.veto) {
        return;
      }
    }


    this.selectedItem = null;
    if (selected) {
      let item = selected;
      while (true) {
        if (item === this.rootItem) {
          this.selectedItem = selected;
          break;
        }
  
        item = item.parent;
        item.open = true;
      }
    }
    this.requestUpdate();
  }

  public findItem(callback:(item:ITreeItem) => boolean, item:ITreeItem = this.rootItem):ITreeItem {
    if (callback(item)) {
      return item;
    }
    for (const child of (item.children || [])) {
      let result = this.findItem(callback, child);
      if (result) {
        return result;
      }
    }
  }

  protected toggleVisibility(item:ITreeItem):void {
    item.open = !item.open;
    this.requestUpdate();
  }

  protected toggleSelected(item:ITreeItem):void {
    let detail:TreeSelectElementEvent = {
      item:item,
      veto:false
    }
    let treeSelectElementEvent = new CustomEvent<TreeSelectElementEvent>('tree-select-item',{detail:detail});
    this.dispatchEvent(treeSelectElementEvent);

    if (detail.veto) {
      return;
    }

    if (this.selectedItem == item) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
    }
    this.requestUpdate();
  }


  protected onDragStart(e:DragEvent, item:ITreeItem) {
    this.dragItem = item;
    e.dataTransfer.effectAllowed = "move";

    let detail:TreeDragElementEvent = {
      item:item,
      target:null,
      action:"start"
    }
    let treeDragElementEvent = new CustomEvent<TreeDragElementEvent>('tree-drag-item',{detail:detail});
    this.dispatchEvent(treeDragElementEvent);

  }


  protected onDragOver(e:DragEvent, targetItem:ITreeItem):boolean {
    let detail:TreeDragElementEvent = {
      item:this.dragItem,
      target:targetItem,
      action:"over",
      veto:true
    }
    let treeDragElementEvent = new CustomEvent<TreeDragElementEvent>('tree-drag-item',{detail:detail});
    this.dispatchEvent(treeDragElementEvent);


    if (!detail.veto) {             //Verificare se è possibile spostare
      e.preventDefault();
      return false;
    }
    return true;
  }

  protected onDrop(e:DragEvent, targetItem:ITreeItem) {
    let detail:TreeDragElementEvent = {
      item:this.dragItem,
      target:targetItem,
      action:"end",
      veto:false
    }
    let treeDragElementEvent = new CustomEvent<TreeDragElementEvent>('tree-drag-item',{detail:detail});
    this.dispatchEvent(treeDragElementEvent);
  }


  protected onAddItem(parentItem:ITreeItem) {
    let treeAddElementEvent = new CustomEvent<TreeAddElementEvent>('tree-add-item',{detail:{parent:parentItem}});
    this.dispatchEvent(treeAddElementEvent);
  }

  protected childOf(parent:ITreeItem, item:ITreeItem):boolean {
    if (parent.children) {
      for (let child of parent.children) {
        if (child === item || this.childOf(child, item)) {
          return true;
        }
      }
    }
    return false;
  }

  renderTreeItem(item:ITreeItem):TemplateResult {
    if (!item) {
      return;
    }

    const iconHidden = !(item.children && item.children.length) && !item.content && this.readonly;
    const iconName = item.open ? this.iconCloseName : this.iconOpenName;

    return html`
      <div class='tree-item'>
        <div class='tree-item-header class=${classMap({selected:(item == this.selectedItem)})}' @click=${() => this.toggleSelected(item)} >
          <k-icon class=${classMap({hidden:iconHidden})} name=${iconName} @click=${() => this.toggleVisibility(item)}></k-icon>
          <div class='tree-item-caption'
               draggable=${this.dragAndDrop} @dragstart=${(e:DragEvent) => this.onDragStart(e, item)} @dragover=${(e:DragEvent) => this.onDragOver(e, item)} @drop=${(e:DragEvent) => this.onDrop(e, item)}
               title=${item.caption}
          >
            ${item.caption}
          </div>
        </div>
        ${item.open ? html`
        <div class='tree-item-content'>
           ${item.content} 
        </div>
        <div class='tree-item-children'>
          ${item.children?.map(child => this.renderTreeItem(child))}
          ${!this.readonly ? this.renderAddItem(item) : null}
        </div>
        `:null}
      </div>
    `
  }

  renderAddItem(parent:ITreeItem):TemplateResult {
    return html`
      <div class='tree-item add'>
        <div class='tree-item-header' >
          <k-icon name=${this.iconAddName} @click=${(e:any) => this.onAddItem(parent)}></k-icon>
          <div class='tree-item-caption'>
            aggiungi elemento...
          </div>
        </div>
      </div>
    `
  }



  render():TemplateResult {
    return html`
      <div class='container'>
        ${this.renderTreeItem(this.rootItem)}
      </div>
    `
  }

}

export interface ITreeItem {
  parent:ITreeItem;
  children:ITreeItem[];

  content?:any;
  caption:any;

  data?:any

  open?:boolean;
}

export interface TreeAddElementEvent {
  parent:ITreeItem;
}

export interface TreeSelectElementEvent {
  item:ITreeItem;
  veto:boolean;
}

export interface TreeDragElementEvent {
  item:ITreeItem;
  target:ITreeItem;
  action:'start' | 'over' | 'end'
  veto?:boolean;
}


